/**
 * File de captures hors ligne du shell mobile (C11, spec § 4).
 * Une capture faite sans réseau est conservée dans IndexedDB et rejouée au retour
 * de la connexion (`captureQueueSlice`). Aucune dépendance : `indexedDB` natif.
 */
import axios from 'axios';
import type { CaptureEntree, CapturePassage } from '../../services/noteAPI';

export interface QueuedCapture {
  id: string;
  text: string;
  /** Citation d'un document (C11 SP3) : envoyée telle quelle au rejeu. */
  passage?: CapturePassage;
  /** Mots-clés ancrés (C11 SP6) : repartent avec la capture au rejeu. */
  entrees?: CaptureEntree[];
  /** Provenance lisible (« Le Capital · Chapitre III ») pour la carte « En attente de réseau ». */
  provenanceLabel?: string;
  /** Commentaire sur un passage (C11, 19/09) : le rejeu doit le redire au serveur. */
  noteType?: 'commentary';
  /** ISO 8601 */
  createdAt: string;
  /** Échecs HTTP consécutifs au rejeu (une erreur réseau n'incrémente pas) */
  attempts: number;
  /** Conservé pour le diagnostic (DevTools → IndexedDB) uniquement — jamais affiché à l'écran. */
  lastError?: string;
}

export const CAPTURE_QUEUE_DB = 'anagogia-capture-queue';
export const CAPTURE_QUEUE_STORE = 'pending';
/** Au-delà, la capture est « en échec » : l'utilisateur choisit Réessayer ou Supprimer. */
export const CAPTURE_QUEUE_MAX_ATTEMPTS = 3;

export interface CaptureQueueStore {
  list(): Promise<QueuedCapture[]>;
  put(item: QueuedCapture): Promise<void>;
  remove(id: string): Promise<void>;
}

const byCreatedAt = (a: QueuedCapture, b: QueuedCapture) => a.createdAt.localeCompare(b.createdAt);

export function newQueuedCapture(
  text: string,
  now: Date = new Date(),
  extra: { passage?: CapturePassage; provenanceLabel?: string; entrees?: CaptureEntree[]; noteType?: 'commentary' } = {},
): QueuedCapture {
  const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${now.getTime().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return {
    id, text, createdAt: now.toISOString(), attempts: 0,
    ...(extra.passage ? { passage: extra.passage } : {}),
    ...(extra.provenanceLabel ? { provenanceLabel: extra.provenanceLabel } : {}),
    ...(extra.entrees && extra.entrees.length > 0 ? { entrees: extra.entrees } : {}),
    ...(extra.noteType ? { noteType: extra.noteType } : {}),
  };
}

export function isFailedCapture(item: QueuedCapture): boolean {
  return item.attempts >= CAPTURE_QUEUE_MAX_ATTEMPTS;
}

/**
 * Erreur réseau (à mettre en file) ou pas (à remonter) : une `AxiosError` sans
 * `response` (le service de capture passe par axios), ou le `TypeError` d'un fetch.
 * Toute réponse HTTP, même 5xx, n'est PAS une erreur réseau.
 */
export function isNetworkError(error: unknown): boolean {
  if (axios.isAxiosError(error)) return !error.response;
  return error instanceof TypeError;
}

export function createMemoryStore(): CaptureQueueStore {
  const items = new Map<string, QueuedCapture>();
  return {
    async list() { return [...items.values()].sort(byCreatedAt); },
    async put(item) { items.set(item.id, { ...item }); },
    async remove(id) { items.delete(id); },
  };
}

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function openDb(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const open = indexedDB.open(dbName, 1);
    open.onupgradeneeded = () => {
      const db = open.result;
      if (!db.objectStoreNames.contains(CAPTURE_QUEUE_STORE)) db.createObjectStore(CAPTURE_QUEUE_STORE, { keyPath: 'id' });
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error);
  });
}

export function createIndexedDbStore(dbName: string = CAPTURE_QUEUE_DB): CaptureQueueStore {
  const withStore = async <T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
    const db = await openDb(dbName);
    try {
      return await request(run(db.transaction(CAPTURE_QUEUE_STORE, mode).objectStore(CAPTURE_QUEUE_STORE)));
    } finally {
      db.close();
    }
  };
  return {
    async list() { return ((await withStore<QueuedCapture[]>('readonly', s => s.getAll())) ?? []).sort(byCreatedAt); },
    async put(item) { await withStore('readwrite', s => s.put({ ...item })); },
    async remove(id) { await withStore('readwrite', s => s.delete(id)); },
  };
}
